import React, { useState, useEffect, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { getAdminOrganizations } from '../../utils/superadminApi';
import OrganizationDetailDrawer from './OrganizationDetailDrawer';

const planColor = (plan) => {
  switch (plan) {
    case 'pro': return 'primary';
    case 'enterprise': return 'secondary';
    default: return 'default';
  }
};

const OrganizationsList = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminOrganizations();
      setOrganizations(data);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.message || 'Failed to load organizations.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleRowClick = (orgId) => {
    setSelectedOrgId(orgId);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ mt: 2, p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Organizations
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table sx={{ minWidth: 650 }} aria-label="organizations table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Members</TableCell>
              <TableCell align="center">Teams</TableCell>
              <TableCell align="center">Active cycles</TableCell>
              <TableCell align="center">Plan</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Last activity</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {organizations.map((org) => {
              const plan = org.plan?.plan || 'free';
              const isLocked = !!(org.plan && org.plan.isLocked);
              return (
                <TableRow
                  key={org.id}
                  hover
                  onClick={() => handleRowClick(org.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell component="th" scope="row">{org.name}</TableCell>
                  <TableCell>{new Date(org.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="center">{org.memberCount}</TableCell>
                  <TableCell align="center">{org.teamCount}</TableCell>
                  <TableCell align="center">{org.activeCycleCount}</TableCell>
                  <TableCell align="center">
                    <Chip label={plan} size="small" color={planColor(plan)} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={isLocked ? 'Locked' : 'Active'}
                      size="small"
                      color={isLocked ? 'error' : 'success'}
                    />
                  </TableCell>
                  <TableCell>
                    {org.lastActivityAt ? new Date(org.lastActivityAt).toLocaleDateString() : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {organizations.length === 0 && !loading && (
        <Typography sx={{ textAlign: 'center', mt: 2 }}>No organizations found.</Typography>
      )}

      <OrganizationDetailDrawer
        orgId={selectedOrgId}
        open={drawerOpen}
        onClose={handleDrawerClose}
        onChanged={fetchOrganizations}
        onDeleted={() => {
          // The org is gone: close the drawer and refresh the list.
          setDrawerOpen(false);
          fetchOrganizations();
        }}
      />
    </Paper>
  );
};

export default OrganizationsList;
